import { Suspense, lazy, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { Loader, NVAlertsBanner, Tabs } from '@nv/basic-components'
import { AdminInstantVotingHeader, ScrollToTop } from '@nv/components'
import {
  AdminUserProvider,
  AriaAnnouncerProvider,
  ConfigurationContext,
  LoginFlowContext,
  NVAlertsProvider,
  SessionTimerProvider,
  ThemeProvider,
  VoterOnboardingContext,
  VoterUserProvider,
  useAdminUserContext,
  useVoterUserContext,
} from '@nv/contexts'
import { useAdminKeycloakAuth, useGetEvent, useVoterKeyCloakAuth } from '@nv/hooks'
import Banner from '@nv/images/banners/portal-banner.png'
import { VoterCandidateProfileViewer, VoterCandidatesAndPositions } from '@nv/pages'
import { NvRoute, VerifyUserGuard } from '@nv/router'
import { nxConfig } from '@nv/utils'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes as Switch, useParams } from 'react-router-dom'
import getConfigurations from '../configurations'
import { pipscTheme } from '../Theme-pipsc'

const Elections = lazy(() => import('@nv/images/nav-icons/elections').then((mod: any) => ({ default: mod.Elections })))
const ElectorsList = lazy(() => import('@nv/images/nav-icons/electors-list').then((mod: any) => ({ default: mod.ElectorsList })))
const MyAccount = lazy(() => import('@nv/images/nav-icons/my-account').then((mod: any) => ({ default: mod.MyAccount })))
const Reports = lazy(() => import('@nv/images/nav-icons/reports').then((mod: any) => ({ default: mod.Reports })))
const Users = lazy(() => import('@nv/images/nav-icons/users').then((mod: any) => ({ default: mod.Users })))
const InstantVoting = lazy(() => import('@nv/images/nav-icons/instant-voting').then((mod: any) => ({ default: mod.InstantVoting })))
const HelpAndSupportLogo = lazy(() =>
  import('@nv/images/nav-icons/help-and-support').then((mod: any) => ({ default: mod.HelpAndSupportLogo })),
)
const Communications = lazy(() => import('@nv/images/nav-icons/communications').then((mod: any) => ({ default: mod.Communications })))

const AdminContestCandidatesHeader = lazy(() => import('@nv/components/lib/admin_contest_candidates_header/AdminContestCandidatesHeader'))
const AdminEventDetailsHeader = lazy(() => import('@nv/components/lib/admin_event_details_header/AdminEventDetailsHeader'))
const BackgroundBanner = lazy(() => import('@nv/components/lib/background_banner/BackgroundBanner'))
const ExtendSessionBanner = lazy(() => import('@nv/components/lib/extend_session_banner/ExtendSessionBanner'))
const LoginTopBar = lazy(() => import('@nv/components/lib/navbars/login_topbar/LoginTopBar'))
const SideBar = lazy(() => import('@nv/components/lib/navbars/sidebar/SideBar'))
const TopBar = lazy(() => import('@nv/components/lib/navbars/topbar/TopBar'))

const AdminCreateInstantVoting = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminCreateInstantVoting })))
const AdminCommunications = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminCommunications })))
const AdminCommunicationsEdit = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminCommunicationsEdit })))
const AdminCommunicationsPreview = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminCommunicationsPreview })))
const AdminCommunicationsTemplates = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminCommunicationsTemplates })))
const AdminContestCandidatesList = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminContestCandidatesList })))
// const AdminContestElectorsList = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminContestElectorsList })))
const AdminEventAttachments = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEventAttachments })))
const AdminEventCommunications = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEventCommunications })))
const AdminEventCommunicationsSettings = lazy(() =>
  import('@nv/pages').then((mod: any) => ({ default: mod.AdminEventCommunicationsSettings })),
)
const AdminEventSettings = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEventSettings })))
const AdminEventOverview = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEventOverview })))
const AdminInstantVotingQuestions = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminInstantVotingQuestions })))
const AdminInstantVotingElectorsList = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminInstantVotingElectorsList })))
const AdminInstantVotingAddQuestion = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminInstantVotingAddQuestion })))
const AdminInstantVotingQuestionDetails = lazy(() =>
  import('@nv/pages').then((mod: any) => ({ default: mod.AdminInstantVotingQuestionDetails })),
)
const AdminLogin = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminLogin })))
const AdminMyAccount = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminMyAccount })))
const AdminReports = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminReports })))
const AdminUserList = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminUserList })))
const AuthAppSetup = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AuthAppSetup })))
const AuthCodeMethod = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AuthCodeMethod })))
const AuthVerify = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AuthVerify })))
const ChangePassword = lazy(() => import('@nv/pages/lib/change_password/ChangePassword'))
const ChangeQRCode = lazy(() => import('@nv/pages/lib/change_qr_code/ChangeQRCode'))
const EventPositions = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.EventPositions })))
const HelpAndSupport = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.HelpAndSupport })))
const OnBoardingCreatePassword = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.OnBoardingCreatePassword })))
const OnBoardingPhoneVerify = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.OnBoardingPhoneVerify })))
const ResetPassword = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.ResetPassword })))
const UserLogin = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.UserLogin })))
const VoterEventDetails = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.VoterEventDetails })))
const VoterInstantVotingQuestions = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.VoterInstantVotingQuestions })))
const VoterInstantVotingBallot = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.VoterInstantVotingBallot })))
const VoterMyAccount = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.VoterMyAccount })))
const VoterMyProfile = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.VoterMyProfile })))
const CandidateProfileReviewer = lazy(() => import('@nv/pages/lib/candidate_profile_reviewer/CandidateProfileReviewer'))

// PUBLIC ROUTES:
const OnBoardingAuthMethodLanding = lazy(() =>
  import('../pages/public/onboarding/onboarding_auth_method_landing/OnBoardingAuthMethodLanding').then((mod: any) => ({
    default: mod.OnBoardingAuthMethodLanding,
  })),
)
const UserLoginLanding = lazy(() =>
  import('../pages/public/user_login_landing/UserLoginLanding').then((mod: any) => ({
    default: mod.UserLoginLanding,
  })),
)

// VOTER ROUTES:
const VoterEventsLanding = lazy(() =>
  import('../pages/voter/voter_events_landing/VoterEventsLanding').then((mod: any) => ({ default: mod.VoterEventsLanding })),
)
const VoterCandidateReviewerLanding = lazy(() =>
  import('../pages/voter/voter_candidate_reviwer_landing/VoterCandidateReviewerLanding').then((mod: any) => ({
    default: mod.VoterCandidateReviewerLanding,
  })),
)
const CandidateProfileLanding = lazy(() =>
  import('../pages/voter/candidate_profile_landing/CandidateProfileLanding').then((mod: any) => ({ default: mod.CandidateProfileLanding })),
)

// ADMINROUTES:
const AdminEventsLanding = lazy(() =>
  import('../pages/admin/admin_events_landing/AdminEventsLanding').then((mod: any) => ({ default: mod.AdminEventsLanding })),
)
const AdminElectorsListLanding = lazy(() =>
  import('../pages/admin/admin_electors_list_landing/AdminElectorsListLanding').then((mod: any) => ({
    default: mod.AdminElectorsListLanding,
  })),
)
const AdminEventElectorsList = lazy(() =>
  import('../pages/admin/admin_event_electors_list/AdminEventElectorsList').then((mod: any) => ({
    default: mod.AdminEventElectorsList,
  })),
)
const AdminInstantVotingLanding = lazy(() =>
  import('../pages/admin/admin_instant_voting_landing/AdminInstantVotingLanding').then((mod: any) => ({
    default: mod.AdminInstantVotingLanding,
  })),
)
const AdminContestElectorsList = lazy(() =>
  import('../pages/admin/admin_contest_electors_list/AdminContestElectorsList').then((mod: any) => ({
    default: mod.AdminContestElectorsList,
  })),
)
export const VoterKeyCloakAuth = () => {
  useVoterKeyCloakAuth()
  return null
}
export const AdminKeycloakAuth = () => {
  useAdminKeycloakAuth()
  return null
}

const VoterLayout = ({ children, tabs }: any) => {
  const { t } = useTranslation()
  const VoterUser = useVoterUserContext().voterUser
  const voterLinks = {
    elections: {
      path: '/voting/voter/dashboard',
      activePaths: ['/voting/voter/event-details'],
      tabLabel: t('pages.events'),
      icon: Elections,
      hasPermissionToView: true,
    },
    CandidateProfileLanding: {
      path: '/voting/voter/candidate-profile',
      tabLabel: t('pages.candidateProfile'),
      icon: Users,
      hasPermissionToView: true,
    },
    ElectionCommittee: {
      path: '/voting/voter/reviewer',
      tabLabel: t('pages.electionCommittee'),
      icon: Users,
      hasPermissionToView: VoterUser?.reviewerNominationEvents?.length,
    },
    help: {
      path: '/voting/voter/help-support',
      tabLabel: t('pages.helpAndSupport'),
      icon: HelpAndSupportLogo,
      hasPermissionToView: true,
    },
    myAccount: {
      path: '/voting/voter/my-account',
      tabLabel: t('pages.myAccount'),
      icon: MyAccount,
      hasPermissionToView: true,
    },
  }

  return (
    <>
      <TopBar links={voterLinks} />
      <div className='main-content-container'>
        <SideBar links={voterLinks} />
        <main>
          <NVAlertsBanner />
          {tabs}
          {children}
        </main>
        {/* <BottomBar links={voterLinks} /> */}
      </div>
    </>
  )
}

const VoterProfileTabLayout = () => {
  const { t } = useTranslation()
  return (
    <Tabs
      tabs={[
        {
          label: t('pages.myProfile'),
          path: '/voting/voter/candidate-profile',
        },
        {
          label: t('pages.myCandidacy'),
          path: '/voting/voter/candidate-profile/events',
        },
      ]}
    />
  )
}

const AdminLayout = ({ children, tabs }: any) => {
  const { t } = useTranslation()
  useAdminUserContext()

  const adminLinks = {
    events: {
      path: '/admin/events',
      tabLabel: t('pages.events'),
      icon: Elections,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isEventModuleAdmin() || KeycloakAdminService.isMonitoringAdmin(),
    },
    electorsList: {
      path: '/admin/electors-list',
      tabLabel: t('pages.electorsList'),
      icon: ElectorsList,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isEventModuleAdmin() || KeycloakAdminService.isContactInfoRoleAdmin(),
    },
    email: {
      path: '/admin/communications/templates',
      tabLabel: t('pages.communications'),
      icon: Communications,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin(),
    },
    // instantVoting: {
    //   path: '/admin/instant-voting',
    //   tabLabel: t('pages.instantVoting'),
    //   icon: InstantVoting,
    //   hasPermissionToView: KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isEventModuleAdmin(),
    // },
    help: {
      path: '/admin/help-support',
      tabLabel: t('pages.helpAndSupport'),
      icon: HelpAndSupportLogo,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin(),
    },
    reports: {
      path: '/admin/reports',
      tabLabel: t('pages.reports'),
      icon: Reports,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isMonitoringAdmin() || KeycloakAdminService.isContactInfoRoleAdmin(),
    },
    adminUsers: {
      path: '/admin/admin-users',
      tabLabel: t('pages.adminUsers'),
      icon: Users,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isContactInfoRoleAdmin(),
    },

    myAccount: {
      path: '/admin/my-account',
      tabLabel: t('pages.myAccount'),
      icon: MyAccount,
      hasPermissionToView: true,
    },
  }
  return (
    <>
      <TopBar links={adminLinks} />
      <div className='main-content-container'>
        <SideBar links={adminLinks} />
        <main>
          <NVAlertsBanner />
          <ExtendSessionBanner />
          {tabs}
          {children}
        </main>
      </div>
    </>
  )
}

const AdminCommunicationTabLayout = () => {
  const { t } = useTranslation()
  return (
    <Tabs
      tabs={[
        {
          label: t('pages.templates'),
          path: '/admin/communications/templates',
        },
        {
          label: t('pages.history'),
          path: '/admin/communications/history',
        },
      ]}
    />
  )
}

const AdminEventTabLayout = () => {
  const { eventId } = useParams()
  useAdminUserContext()
  const event = useGetEvent(eventId)

  const { t } = useTranslation()
  const tabs =
    KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isEventModuleAdmin()
      ? [
          {
            label: t('events.labels.overview'),
            path: `/admin/events/${eventId}/events-overview`,
          },
          {
            label: t('events.labels.positions'),
            path: `/admin/events/${eventId}/event-positions`,
          },
          {
            label: t('admin.electorsList.electorsList'),
            path: `/admin/events/${eventId}/electors-list/${event?.tiviId}`,
          },
          {
            label: t('events.labels.attachments'),
            path: `/admin/events/${eventId}/attachments`,
          },
          {
            label: t('events.labels.nominationsSettings'),
            path: `/admin/events/${eventId}/settings`,
          },
          {
            label: t('events.labels.communicationsSettings'),
            path: `/admin/events/${eventId}/communications-settings`,
          },
          {
            label: t('pages.communications'),
            path: `/admin/events/${eventId}/communications`,
          },
        ]
      : KeycloakAdminService.isMonitoringAdmin()
        ? [
            {
              label: t('events.labels.overview'),
              path: `/admin/events/${eventId}/events-overview`,
            },
            {
              label: t('events.labels.positions'),
              path: `/admin/events/${eventId}/event-positions`,
            },
            {
              label: t('admin.electorsList.electorsList'),
              path: `/admin/events/${eventId}/electors-list/${event?.tiviId}`,
            },
            {
              label: t('events.labels.attachments'),
              path: `/admin/events/${eventId}/attachments`,
            },
            {
              label: t('events.labels.nominationsSettings'),
              path: `/admin/events/${eventId}/settings`,
            },
          ]
        : KeycloakAdminService.isContactInfoRoleAdmin()
          ? [
              {
                label: t('events.labels.overview'),
                path: `/admin/events/${eventId}/events-overview`,
              },

              {
                label: t('events.labels.attachments'),
                path: `/admin/events/${eventId}/attachments`,
              },
            ]
          : []
  return (
    <>
      <AdminEventDetailsHeader eventId={eventId} />
      <Tabs tabs={tabs} />
    </>
  )
}

const AdminContestCandidatesTabLayout = () => {
  const { eventId, qid } = useParams()

  const { t } = useTranslation()
  const tabs = [
    {
      label: t('events.labels.candidates'),
      path: `/admin/events/${eventId}/event-positions/${qid}/candidates`,
    },

    {
      label: t('admin.electorsList.electorsList'),
      path: `/admin/events/${eventId}/event-positions/${qid}/electors-list`,
    },
  ]
  return (
    <>
      <AdminContestCandidatesHeader eventId={eventId} />
      <Tabs tabs={tabs} />
    </>
  )
}

const VoterReviewerContestCandidatesTabLayout = () => {
  const { eventId, qid } = useParams()

  const { t } = useTranslation()
  const tabs = [
    {
      label: t('events.labels.candidates'),
      path: `/voting/voter/reviewer/event-details/${eventId}/event-positions/${qid}/candidates`,
    },

    {
      label: t('admin.electorsList.electorsList'),
      path: `/voting/voter/reviewer/event-details/${eventId}/event-positions/${qid}/electors-list`,
    },
  ]
  return (
    <>
      <AdminContestCandidatesHeader eventId={eventId} />
      <Tabs tabs={tabs} />
    </>
  )
}

const AdminInstantVotingDetailsTabLayout = () => {
  const { eventId } = useParams()

  const { t } = useTranslation()
  const tabs = [
    {
      label: t('events.labels.questions'),
      path: `/admin/instant-voting/${eventId}/instant-voting-details/questions`,
    },
    {
      label: t('admin.electorsList.electorsList'),
      path: `/admin/instant-voting/${eventId}/instant-voting-details/electors-list`,
    },
  ]
  return (
    <>
      <AdminInstantVotingHeader eventId={eventId} />
      <Tabs tabs={tabs} />
    </>
  )
}

const PublicLayout = ({ children }) => {
  const [authMethod, setAuthMethod] = useState(null)
  const reCaptchaKey = nxConfig.RECAPTCHA_SITE_KEY

  return (
    <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
      <VoterOnboardingContext.Provider value={{ authMethod, setAuthMethod }}>
        <LoginTopBar />
        <BackgroundBanner banner={Banner} />
        <main>
          <NVAlertsBanner />
          {children}
        </main>
      </VoterOnboardingContext.Provider>
    </GoogleReCaptchaProvider>
  )
}

const RoutesWrapper = () => {
  const [loginFlow, setLoginFlow] = useState('Login')
  const { t } = useTranslation()
  const configurations = getConfigurations({ t })

  return (
    <SessionTimerProvider>
      <VoterUserProvider>
        <AdminUserProvider>
          <AriaAnnouncerProvider>
            <LoginFlowContext.Provider value={{ loginFlow, setLoginFlow }}>
              <ConfigurationContext.Provider value={configurations}>
                <ThemeProvider initialTheme={pipscTheme}>
                  <NVAlertsProvider>
                    <ScrollToTop />
                    <Routes />
                  </NVAlertsProvider>
                </ThemeProvider>
              </ConfigurationContext.Provider>
            </LoginFlowContext.Provider>
          </AriaAnnouncerProvider>
        </AdminUserProvider>
      </VoterUserProvider>
    </SessionTimerProvider>
  )
}

const Routes = () => {
  // prettier-ignore
  return (
    <>
      <Switch>
        <Route path={'/admin/*'} element={<AdminKeycloakAuth />} />
        <Route path='*' element={<VoterKeyCloakAuth />} />
      </Switch>

      <Suspense fallback={<Loader />}>
        <Switch>
          {/* ADMIN ROUTES */}
          <Route path='/admin/dashboard' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventsLanding} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/events' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventsLanding} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/electors-list' element={<NvRoute render={() => <VerifyUserGuard component={AdminElectorsListLanding} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/admin-users' element={<NvRoute render={() => <VerifyUserGuard component={AdminUserList} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/reports' element={<NvRoute render={() => <VerifyUserGuard component={AdminReports} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/my-account' element={<NvRoute render={() => <VerifyUserGuard component={AdminMyAccount} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/help-support' element={<NvRoute render={() => <VerifyUserGuard component={HelpAndSupport} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/change-password' element={<NvRoute render={() => <VerifyUserGuard component={() => <ChangePassword isAdmin={true} />} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/change-qr-code' element={<NvRoute render={() => <VerifyUserGuard component={() => <ChangeQRCode admin={true} />} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/events/:eventId/events-overview' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventOverview} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/event-positions' element={<NvRoute render={() => <VerifyUserGuard component={EventPositions} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/electors-list/:tiviId' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventElectorsList} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/attachments' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventAttachments} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/settings' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventSettings} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/communications-settings' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventCommunicationsSettings} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/communications' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventCommunications} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />} />

          <Route path='/admin/events/:eventId/event-positions/:qid/candidates' element={<NvRoute render={() => <VerifyUserGuard component={AdminContestCandidatesList} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminContestCandidatesTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/event-positions/:qid/electors-list' element={<NvRoute render={() => <VerifyUserGuard component={AdminContestElectorsList} redirectURL={'/admin/login'} />}  layout={<AdminLayout tabs={<AdminContestCandidatesTabLayout />} />} />} />
          <Route path='/admin/events/:eventId/event-positions/:qid/candidates/:candidateId' element={<NvRoute render={() => <VerifyUserGuard component={CandidateProfileReviewer} redirectURL={'/admin/login'} />} layout={AdminLayout}/>} />

          <Route path='/admin/communications/history' element={<NvRoute render={() => <VerifyUserGuard component={AdminCommunications} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdminCommunicationTabLayout />} />} />} />
          <Route path='/admin/communications/templates' element={<NvRoute render={() => <VerifyUserGuard component={AdminCommunicationsTemplates} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdminCommunicationTabLayout />} />} />} />
          <Route path='/admin/edit-communication/' element={<NvRoute render={() => <VerifyUserGuard component={AdminCommunicationsEdit} redirectURL={'/admin/login'} />} layout={<AdminLayout />}/>} />
          <Route path='/admin/edit-communication/:templateId' element={<NvRoute render={() => <VerifyUserGuard component={AdminCommunicationsEdit} redirectURL={'/admin/login'} />} layout={<AdminLayout />}/>} />
          <Route path='/admin/preview-communication/:templateId' element={<NvRoute render={() => <VerifyUserGuard component={AdminCommunicationsPreview} redirectURL={'/admin/login'} />} layout={<AdminLayout />}/>} />
          <Route path='/admin/instant-voting/' element={<NvRoute render={() => <VerifyUserGuard component={AdminInstantVotingLanding} redirectURL={'/admin/login'} />} layout={<AdminLayout />}/>} />
          <Route path='/admin/create-instant-voting' element={<NvRoute render={() => <VerifyUserGuard component={AdminCreateInstantVoting} redirectURL={'/admin/login'} />} layout={<AdminLayout />}/>} />
          <Route path='/admin/edit-instant-voting/:eventId' element={<NvRoute render={() => <VerifyUserGuard component={AdminCreateInstantVoting} redirectURL={'/admin/login'} />} layout={<AdminLayout />}/>} />
          <Route path='/admin/instant-voting/:eventId/instant-voting-details/questions' element={<NvRoute render={() => <VerifyUserGuard component={AdminInstantVotingQuestions} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdminInstantVotingDetailsTabLayout />} />} />} />
          <Route path='/admin/instant-voting/:eventId/instant-voting-details/electors-list' element={<NvRoute render={() => <VerifyUserGuard component={AdminInstantVotingElectorsList} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdminInstantVotingDetailsTabLayout />} />} />} />
          <Route path='/admin/instant-voting/:eventId/:eventName/instant-voting-details/question-details/:questionId' element={<NvRoute render={() => <VerifyUserGuard component={AdminInstantVotingQuestionDetails} redirectURL={'/admin/login'} />} layout={<AdminLayout />} />} />
          <Route path='/admin/instant-voting/:eventId/:eventName/instant-voting-details/add-question' element={<NvRoute render={() => <VerifyUserGuard component={AdminInstantVotingAddQuestion} redirectURL={'/admin/login'} />} layout={<AdminLayout />} />} />
          <Route path='/admin/instant-voting/:eventId/:eventName/instant-voting-details/edit-question/:questionId' element={<NvRoute render={() => <VerifyUserGuard component={AdminInstantVotingAddQuestion} redirectURL={'/admin/login'} />} layout={<AdminLayout />} />} />

          {/* VOTER ROUTES */}
          <Route path='/voting/voter/dashboard' element={<NvRoute render={() => <VerifyUserGuard component={VoterEventsLanding} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/my-account' element={<NvRoute render={() => <VerifyUserGuard component={VoterMyAccount} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/help-support' element={<NvRoute render={() => <VerifyUserGuard component={HelpAndSupport} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/change-qr-code' element={<NvRoute render={() => <VerifyUserGuard component={ChangeQRCode} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/change-password' element={<NvRoute render={() => <VerifyUserGuard component={ChangePassword} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/candidate-profile' element={<NvRoute render={() => <VerifyUserGuard component={VoterMyProfile} redirectURL={'/'} />}  layout={<VoterLayout tabs={<VoterProfileTabLayout />} />} />} />
          <Route path='/voting/voter/candidate-profile/events' element={<NvRoute render={() => <VerifyUserGuard component={CandidateProfileLanding} redirectURL={'/'} />}  layout={<VoterLayout tabs={<VoterProfileTabLayout />} />} />} />
          <Route path='/voting/voter/candidate-profile/events/:eventId' element={<NvRoute render={() => <VerifyUserGuard component={VoterMyProfile} redirectURL={'/'} />}  layout={<VoterLayout />} />} />
          <Route path='/voting/voter/candidate-profile/events/:eventId/questions/:qid' element={<NvRoute render={() => <VerifyUserGuard component={VoterMyProfile} redirectURL={'/'} />}  layout={<VoterLayout />} />} />

          <Route path='/voting/voter/event-details/:eventId' element={<NvRoute render={() => <VerifyUserGuard component={VoterEventDetails} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/event-details/:eventId/candidates-and-positions' element={<NvRoute render={() => <VerifyUserGuard component={VoterCandidatesAndPositions} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/event-details/:eventId/questions/:qid/candidates/:candidateId' element={<NvRoute render={() => <VerifyUserGuard component={VoterCandidateProfileViewer} redirectURL={'/'} />} layout={VoterLayout} />} />

          {/* Voter Candidate Reviewer Routes */}
          <Route path='/voting/voter/reviewer' element={<NvRoute render={() => <VerifyUserGuard component={VoterCandidateReviewerLanding} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/reviewer/event-details/:eventId/event-positions' element={<NvRoute render={() => <VerifyUserGuard component={EventPositions} redirectURL={'/'} />}  layout={VoterLayout} />} />
          <Route path='/voting/voter/reviewer/event-details/:eventId/event-positions/:qid/candidates' element={<NvRoute render={() => <VerifyUserGuard component={AdminContestCandidatesList} redirectURL={'/'} />}  layout={<VoterLayout tabs={<VoterReviewerContestCandidatesTabLayout />} />} />} />
          <Route path='/voting/voter/reviewer/event-details/:eventId/event-positions/:qid/electors-list' element={<NvRoute render={() => <VerifyUserGuard component={AdminContestElectorsList} redirectURL={'/'} />}  layout={<VoterLayout tabs={<VoterReviewerContestCandidatesTabLayout />} />} />} />
          <Route path='/voting/voter/reviewer/event-details/:eventId/event-positions/:qid/candidates/:candidateId' element={<NvRoute render={() => <VerifyUserGuard component={CandidateProfileReviewer} redirectURL={'/'} />} layout={VoterLayout}/>} />

          <Route path='/voting/voter/instant-voting/:eventId' element={<NvRoute render={() => <VerifyUserGuard component={VoterInstantVotingQuestions} redirectURL={'/'} />} layout={VoterLayout} />} />
          <Route path='/voting/voter/instant-voting/ballot/:eventId/:questionId' element={<NvRoute render={() => <VerifyUserGuard component={VoterInstantVotingBallot} redirectURL={'/'} />} layout={VoterLayout} />} />

          {/* PUBLIC ROUTES */}
          <Route path='/' element={<NvRoute component={UserLoginLanding} layout={PublicLayout} />}/>
          <Route path='/voting/voter/login' element={<NvRoute component={UserLogin} layout={PublicLayout} />}/>

          <Route path='/voting/voter-verify/select-verifier' element={<NvRoute component={AuthCodeMethod} layout={PublicLayout} />} />
          <Route path='/voting/voter-verify/verify' element={<NvRoute component={AuthVerify} layout={PublicLayout} />} />
          <Route path='/voting/voter-auth/reset-password' element={<NvRoute component={ResetPassword} layout={PublicLayout} />} />
          <Route path='/voting/voter-auth/create-password' element={<NvRoute component={OnBoardingCreatePassword} layout={PublicLayout} />} />
          <Route path='/voting/voter-onboarding/phone-verify' element={<NvRoute component={OnBoardingPhoneVerify} layout={PublicLayout} />} />
          <Route path='/voting/voter-onboarding/auth-method' element={<NvRoute component={OnBoardingAuthMethodLanding} layout={PublicLayout} />} />
          <Route path='/voting/voter-auth/auth-app-setup' element={<NvRoute component={AuthAppSetup} layout={PublicLayout} />} />
          <Route path='/voting/voter-auth/change-password' element={<NvRoute component={ChangePassword} layout={PublicLayout} />} />

          <Route path='/admin' element={<NvRoute component={AdminLogin} layout={PublicLayout} />} />
          <Route path='/admin/login' element={<NvRoute component={AdminLogin} layout={PublicLayout} />} />
          <Route path='/admin/auth/verify' element={<NvRoute component={AuthVerify} layout={PublicLayout} />} />
          <Route path='/admin/auth/auth-app-setup' element={<NvRoute component={AuthAppSetup} layout={PublicLayout} />} />
          <Route path='/admin/auth/change-password' element={<NvRoute component={() => <ChangePassword isAdmin={true} />} layout={PublicLayout} />} />
          <Route path='/admin/auth/reset-password' element={<NvRoute component={ResetPassword} layout={PublicLayout} />} />

          <Route path='/voting/voter/*' element={<NvRoute render={() => <Navigate to='/voting/voter/dashboard' />} />} />
          <Route path='/admin/*' element={<NvRoute render={() => <Navigate to='/admin/events' />} />} />
          <Route path='*' element={<Navigate to='/' />} />
        </Switch>
      </Suspense>
    </>
  )
}
export { RoutesWrapper as Routes }
